/* @flow */
import React, { Component, PureComponent } from 'react'
import ReactDOM from 'react-dom'
import { scaleOrdinal, scaleTime, scaleLinear, ScaleTime, ScaleLinear, ScaleOrdinal } from 'd3-scale'
import { bisector } from 'd3-array'
import { select, selectAll, event, mouse } from 'd3-selection'
import { brushX, BrushBehaviour } from 'd3-brush'
import { line } from 'd3-shape'
import { axisBottom, axisLeft } from 'd3-axis'
import { format } from 'd3-format'
import { timeYear } from 'd3-time'

import moment from 'moment'

import TimeSlots from './TimeSlots'
import DatePicker from './DatePicker'
import Legend from './Legend'
import Dashboard from './Dashboard.js'
import InitBox from './InitBox.js'
import { ToggleIcon } from './List.js'
import { twoDec, round10 } from './utils'
import { agColors, changeColor, chartColors, multiFormat, formatDate, capitalize } from './globals'
import labels from './labels'

import type {Data, ActiveFilters, Lang, Mode, Initial} from './App'
import type { Display } from './ChartContainer'
import type { LegendData } from './Legend'

export type DateRange = [Date, Date]

type Margin = {
  top: number,
  right: number,
  bottom: number,
  left: number
}

type Value = {
  date: Date,
  valuation: number,
  change: number
}

type Values = Array<Value>

type ChartData = Array<{
  id: string,
  name: string,
  values: Values
}>

type TooltipData = Array<{
  id: string,
  name: string,
  date: Date,
  valuation: number,
  change: number
}>

type YValue = 'change' | 'valuation'

type Props = {
  appId:string,
  width: number,
  height: number,
  margin: Margin,
  margin2: Margin,
  data: Data,
  initDateRange: ?DateRange,
  display: Display,
  urlTemplate: string,
  lang: Lang,
  activeFilters: ActiveFilters,
  mode: Mode,
  initial: Initial
}

type State = {
  unselected: Set<string>,
  status: 'filter' | 'select' | 'changeY' | 'brushing' ,
  yValue: YValue,
  timeSlot: ?number
}

export const colors: ScaleOrdinal = scaleOrdinal(chartColors)

const bisect = bisector(({date}) => date).left
const withinDomain = (date: Date, [start, end]: DateRange) => moment(date).isSameOrAfter(start) && moment(date).isSameOrBefore(end)
const calculateChange = (val0: number, val: number): number => (val - val0) * 100 / val0

class Chart extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      unselected: new Set(),
      status: 'filter',
      yValue: 'change',
      timeSlot: 12
    }
  }
  svg: SVGSVGElement
  node: SVGSVGElement
  clip: SVGSVGElement
  focus: SVGSVGElement
  lineGroup: SVGSVGElement
  xAxis: SVGSVGElement
  yAxis: SVGSVGElement
  x2Axis: SVGSVGElement
  contextArea: SVGSVGElement
  contextLines: SVGSVGElement
  brushContext: SVGSVGElement
  mouseG: SVGSVGElement
  mouseGRect: SVGSVGElement
  hoverLine: SVGSVGElement
  tooltip: SVGSVGElement
  x: ScaleTime<number, number>
  x2: ScaleTime<number, number>
  y: ScaleLinear<number, number>
  y2: ScaleLinear<number, number>
  brush: BrushBehaviour
  firstRender: boolean = true
  header: ?HTMLElement
  chart: ?HTMLElement
  dateRange: DateRange = [new Date(), new Date()]
  brushWidth: number = -1

  componentDidMount () {
    this.start()
  }
  
  componentDidUpdate ({ width: prevWidth, data: prevData }: Props) {
    const { width, data, initDateRange } = this.props
    const sizeChanged = width !== 0 && round10(prevWidth, -5) !== round10(width, -5)
    const dataChanged = prevData.length !== data.length
    if (this.firstRender && dataChanged) {
      this.firstRender = false
      const { timeSlot } = this.state
      this.updateContext()
      let [start, end] = this.x2.domain()
      if (initDateRange) {
        [start, end] = initDateRange
        this.filterDate([start, end])
      }
      else if (timeSlot) {
        const startMom = moment(end).subtract(timeSlot, 'months')
        startMom.isAfter(start) && (start = startMom.toDate())
        this.filterDate([start, end], timeSlot)
      }
    } else {
      this.update(sizeChanged, dataChanged)
    }
  }

  start () {
    const { appId } = this.props
    const thisNode = ReactDOM.findDOMNode(this)
    if (thisNode instanceof HTMLElement) {
      this.header = thisNode.querySelector('.ac-chart__header')
      this.chart = thisNode.querySelector('.ac-chart__chart')
    }
    this.svg = select(ReactDOM.findDOMNode(this)).select('svg')
    this.clip = this.svg.append('defs').append('clipPath')
      .attr('id', 'clip')
      .append('rect')
    
    this.focus = this.svg.append('g')
      .attr('class', 'focus')
    this.xAxis = this.focus.append('g')
      .attr('class', 'axis')
    this.yAxis = this.focus.append('g')
      .attr('class', 'axis')
    this.lineGroup = this.focus.append('g')

    this.mouseG = this.focus.append('g')
      .attr('class', 'mouse-over-effects')
    this.hoverLine = this.mouseG.append('path')
      .style('stroke', 'black')
      .style('stroke-width', '1px')
      .style('opacity', '0')
    this.tooltip = select(`#${appId} .ac-chart-tooltip`)
    const that = this
    this.mouseGRect = this.mouseG.append('rect')
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .on('mouseout', this.mouseout)
      .on('mouseover', this.mouseover)
      .on('mousemove', function() {
        const evt = event
        const ms = mouse(this)
        setTimeout (() => that.mousemove(...ms, evt))
      })

    this.contextArea = this.svg.append('g')
      .attr('class', 'context')
    this.x2Axis = this.contextArea.append('g')
      .attr('class', 'axis')
    this.contextLines = this.contextArea.append('g')
      .attr('class', 'contextLines')
    this.brushContext = this.contextArea.append('g')
      .attr('class', 'brush')

    this.x = scaleTime()
    this.x2 = scaleTime()
    this.y = scaleLinear()
    this.y2 = scaleLinear()

    this.brush = brushX()
      .on('brush end', this.throttle(100, this.brushed))
  }

  throttle (ms: number, callback: () => void) {
    let lastCall = 0
    return () => {
      const now = new Date().getTime()
      const diff = now - lastCall
      const evt = event
      if (diff >= ms) {
        lastCall = now
        setTimeout(() => callback(evt))
      }
    }
  }

  update (sizeChanged: boolean = true, dataChanged: boolean = true) {
    const { status } = this.state
    const { dateRange } = this
    sizeChanged && this.updateSize()
    if (sizeChanged || dataChanged || status === 'select' || status === 'changeY') {
      this.updateContext()
      this.correctDateRange()
    } else if (status === 'filter') {
      this.moveBrusher()
    } 
    this.updateFocus(dateRange)
  }

  updateSize () {
    const { width, height, height2, margin, margin2 } = this.extractSize()
    this.svg
      .attr('width', this.props.width)
      .attr('height', this.props.height)
    
    this.clip
      .attr('width', width)
      .attr('height', height)
    
    this.focus
      .attr('transform', `translate(${margin.left}, ${margin.top})`)
    
    this.xAxis
      .attr('transform', `translate(0, ${height})`)

    this.contextArea
      .attr('transform', `translate(${margin2.left}, ${margin2.top})`)
    
    this.x2Axis
      .attr('transform', `translate(0, ${height2})`)
    
    this.brush
      .extent([[0, 0], [width, height2]])

    this.mouseGRect 
      .attr('width', width)
      .attr('height', height)

    this.x2.range([0, width])
    this.y2.range([height2, 0])
    this.x.range([0, width])
    this.y.range([height, 0])
  }

  updateLines (mainLines: boolean, data: ChartData) {
    const { appId } = this.props
    const { status } = this.state
    const lines = (mainLines ? this.lineGroup : this.contextLines).selectAll(`#${appId} .chart-line`)
      .data(data, ({ id }) => id)
    // exit 
    lines.exit().remove()
    // enter
    const lineEnter = lines.enter().append('path')
      .attr('class', 'chart-line')
      .attr('fill', 'none')
      .attr('stroke-width', '1.5px')
      .style('stroke', ({ id }) => colors(id))
    // enter + update
    const merged = mainLines && status !== 'brushing' ? lineEnter.merge(lines).transition() : lineEnter.merge(lines)
    merged.attr('d', ({ values }) => this.line(values, mainLines))
  }

  line (values: Values, mainLines: boolean) {
    const { yValue } = this.state
    const ln = line()
      .x(d => (mainLines ? this.x : this.x2)(d.date))
      .y(d => (mainLines ? this.y : this.y2)(d[yValue]))
    return ln(values)
  }

  moveBrusher = (dateRange: DateRange = this.dateRange) => {
    this.brushContext
      .call(this.brush)
      .call(this.brush.move, dateRange.map(this.x2))
    this.brushWidth = this.getBrusherWidth()
  }

  getBrusherWidth = () => {
    const { chart } = this.props.display
    return chart ? round10(this.brushContext.select('.selection').attr('width')) : 0
  }

  brushed = ({ selection, sourceEvent }) => {
    if (!selection || !sourceEvent || !sourceEvent.isTrusted) return

    let [start, end] = selection.map(this.x2.invert, this.x2).map(d => moment(d).startOf('day').toDate())
    let { timeSlot } = this.state
    const brushWidth = this.getBrusherWidth()
    const diff = this.brushWidth - brushWidth
    this.brushWidth = brushWidth
    
    if (!diff && timeSlot) {
      start = moment(end).subtract(timeSlot, 'months').toDate()
    } else {
      timeSlot = null
    }

    const dateRange = [start, end]
    this.dateRange = dateRange

    this.setState(() => ({ status: 'brushing', timeSlot }))
  }

  updateContext () {
    this.updateX2DomainAndAxis()
    const data = this.updateY2Domain()
    this.updateLines(false, data)
  }

  updateFocus (dateRange: DateRange) {
    const { width, height } = this.extractSize()
    const { lang, appId } = this.props
    const ticks = width < 600 ? 4 : null
    const { data, xDomain, yDomain } = this.dataToDisplay(dateRange)
    this.x.domain(xDomain)
    this.xAxis
      .call(axisBottom(this.x).ticks(ticks).tickFormat(multiFormat(lang)).tickSize(-height))

    this.xAxis
      .select('.domain').remove()
    this.y.domain(yDomain)
    this.yAxis
      .call(axisLeft(this.y).tickSize(-width).tickFormat(format(".2f")).tickSizeOuter(0))
      .selectAll(`#${appId} .tick line`).each(function (d) {
        const yVal = Number(this.nextElementSibling.textContent)
        !yVal && this.setAttribute('style', 'stroke: #000; shape-rendering: auto;')
      })
    
    this.updateLines(true, data)
    this.updateCirclesAndLabels(data)
  }

  dataToDisplay ([dateL, dateR]: DateRange): { xDomain: DateRange, yDomain: [number, number], data: ChartData } {
    const { data } = this.props
    const { unselected, yValue } = this.state
    let [dateMin, dateMax] = [null, null]
    let [valYmin, valYmax] = [Infinity, -Infinity]
    const displayData: ChartData = []
    for (const { id, name, values } of data) {
      if (unselected.has(id)) {
        continue
      }
      let iL = bisect(values, dateL)
      let { date: startDate } = values[iL]
      // when we don't have data for the searched day(ex. sunday or holiday), take data from the latest day before we have data
      iL && formatDate(dateL) !== formatDate(startDate) && (startDate = values[--iL].date)
      if (moment(startDate).isSameOrAfter(dateR)) {
        continue
      }
      (!dateMin || moment(startDate).isBefore(dateMin, 'day')) && (dateMin = startDate)
      const { valuation: val0 } = values[iL]
      const newData = { id, name, values: []}
      for (let i = iL; i < values.length; i++) {
        const { date, valuation } = values[i]
        const change = calculateChange(val0, valuation)
        const value = yValue === 'change' ? change : valuation
        value < valYmin && (valYmin = value)
        value > valYmax && (valYmax = value)

        newData.values.push({date, change, valuation})
        if (moment(date).isSameOrAfter(dateR, 'day')) {
          !dateMax && (dateMax = date)
          break
        }
      }
      displayData.push(newData)
    }
    return {
      data: displayData,
      xDomain: [dateMin || dateL, dateMax || dateR],
      yDomain: [valYmin, valYmax]
    }
  }

  updateY2Domain (): ChartData {
    const { data } = this.props
    const { yValue, unselected } = this.state
    let [valYmin, valYmax] = [Infinity, -Infinity]
    let newData = []
    for (const { id, name, values } of data) {
      if (unselected.has(id)) {
        continue
      }
      const val0 = values[0].valuation
      const newFundData = { id, name, values: []}
      for (const { date, valuation } of values) {
        const change = calculateChange(val0, valuation)
        const value = yValue === 'change' ? change : valuation
        newFundData.values.push({ date, change, valuation })
        if (value < valYmin) {
          valYmin = value
        } else if (value > valYmax) {
          valYmax = value
        }
      }
      newData.push(newFundData)
    }
    this.y2.domain([valYmin, valYmax])
    return newData
  }

  findXDomainEdges (data: Data = this.props.data): DateRange {
    const { unselected } = this.state
    let dateMin, dateMax

    for ( const { id, values } of data) {
      if (unselected.has(id)) {
        continue
      }
      const [dateL, dateR] = [values[0].date, values[values.length - 1].date]
      if (!dateMin || moment(dateL).isBefore(dateMin)) {
        dateMin = dateL
      }
      if (!dateMax || moment(dateR).isAfter(dateMax)) {
        dateMax = dateR
      }
    }
    return dateMin && dateMax ? [dateMin, dateMax] : this.x2.domain()
  }

  updateX2DomainAndAxis () {
    const { lang } = this.props
    const { width } = this.extractSize()
    this.x2.domain(this.findXDomainEdges())
    this.x2Axis
      .call(axisBottom(this.x2).ticks(width < 800 ? timeYear : null).tickFormat(multiFormat(lang)))
  }

  correctDateRange () {
    const { dateRange } = this
    const { timeSlot } = this.state
    const x2Domain = this.x2.domain()
    let correctionNeeded = false
    if (!withinDomain(dateRange[0], x2Domain)) {
      dateRange[0] = x2Domain[0]
      correctionNeeded = true
    }
    if (!withinDomain(dateRange[1], x2Domain)) {
      dateRange[1] = x2Domain[1]
      correctionNeeded = true
    }
    this.setState({ status: 'filter', timeSlot: !this.firstRender && correctionNeeded ? null : timeSlot })
  }

  updateCirclesAndLabels (data: ChartData) {
    const { appId } = this.props
    const mousePerLine = this.mouseG.selectAll(`#${appId} .mouse-per-line`)
      .data(data, d => d.id)

    mousePerLine.exit().remove()
    mousePerLine.enter()
      .append('g')
        .attr('class', 'mouse-per-line')
      .append('circle')
        .attr('r', 4)
        .attr('pointer-events', 'none')
        .style('stroke', d => colors(d.id))
        .style('fill', d => colors(d.id))
        .style('stroke-width', '1px')
        .style('opacity', '0')
  }

  mouseout = () => {
    const { appId } = this.props
    this.hoverLine
      .style('opacity', 0)
    this.tooltip
      .style('opacity', 0)
      .style('z-index', -1)
    selectAll(`#${appId} .mouse-per-line circle`)
      .style('opacity', 0)
  }

  mouseover = () => {
    const { appId, data } = this.props
    if (!data || !data.length) { return }
    this.hoverLine
      .style('opacity', 1)
    this.tooltip
      .style('opacity', 0.9)
      .style('z-index', 10)
    selectAll(`#${appId} .mouse-per-line circle`)
      .style('opacity', 1)
  }

  mousemove = (x: number, y: number, event) => {
    const { appId } = this.props
    const { height } = this.extractSize()
    const { yValue } = this.state
    const sortBy: YValue = yValue === 'change' ? 'change' : 'valuation'
    const tooltipData: TooltipData = []
    let posX = x
    selectAll(`#${appId} .mouse-per-line`)
      .attr('transform', ({ id, name, values }) => {
        const date = this.x.invert(x)
        const index = bisect(values, date)
        let posY = -100
        if (index) {
          const valueL = values[index - 1]
          const valueR = values[index] || values[index - 1]
          const xL = this.x(valueL.date)
          const xR = this.x(valueR.date)
          if (x - xL < xR - x) {
            posX = xL
            posY = this.y(valueL[yValue])
            tooltipData.push({ id, name, ...valueL })
          } else {
            posX = xR
            posY = this.y(valueR[yValue])
            tooltipData.push({ id, name, ...valueR })
          }
        }
        return `translate(${posX}, ${posY})`
      })

    if (tooltipData.length > 0) {
      this.hoverLine
        .attr('d', d => `M${posX},${height} ${posX}, 0`)

      const space = 5
      const leftMargin = event.layerX - x
      let [xt, yt] = [leftMargin + posX, 0]
      const tooltip = this.tooltip.select('div').node()

      if (tooltip) {
        const { width, height } = tooltip.getBoundingClientRect()
        const { width: parentWidth } = tooltip.parentNode.parentNode.getBoundingClientRect()
        if (xt - space < leftMargin + width && xt + space + width > parentWidth) {
          xt = leftMargin + space
        } else if (xt - space > leftMargin + width) {
          xt = xt - space - width
        } else {
          xt = xt + space
        }
        yt = event.layerY - height / 2
      }
      const tooltipContent = `
        <table class='ui table unstackable'>
          <tbody>
            <tr>
              <td colspan='4'>${moment(tooltipData[0].date).format('L')}</td>
            </tr>
            ${tooltipData.sort((a, b) => a[sortBy] < b[sortBy] ? 1 : -1).map(({id, name, change, valuation}) => {
              change = twoDec(change)
              valuation = twoDec(valuation)
              return `<tr>
                <td><div class='ac-chart-tooltip__shape' style='background-color:${colors(id)}'></div></td>
                <td>${name}</td>
                <td class='right aligned ${changeColor(change)}'>${change}%</td>
                <td class='right aligned'>${valuation}</td>
              </tr>`
            }).join('')}
          </tbody>
        </table>`

      this.tooltip
        .style('left', `${xt}px`)
        .style('top', `${yt}px`)
        .html(`<div>${tooltipContent}</div>`)
    }
  }

  extractSize () {
    const { margin, margin2, width: widthWithMargins, height: heightWithMargins } = this.props
    const width = widthWithMargins - margin.left - margin.right
    const height = heightWithMargins - margin.top - margin.bottom
    const height2 = heightWithMargins - margin2.top - margin2.bottom
    return { width, height, height2, margin, margin2 }
  }

  filterDate = (dateRange: DateRange, timeSlot: ?number = null) => {
    this.dateRange = dateRange
    this.setState({ status: 'filter', timeSlot })
  }

  handleLegendClick = (id: string) => (event: SyntheticEvent<>): void =>  {
    const { unselected } = this.state
    unselected.has(id) ? unselected.delete(id) : unselected.add(id)
    this.setState({ unselected, status: 'select' })
  }

  changeYValue = (name: YValue) => () => {
    this.setState({ yValue: name, status: 'changeY' })
  }

  unselected = (id: string, name: string) => {
    const {data} = this.props;
    let sfdrLevel = 0;
    for (const item of data) {
      if (item.id === id && item.name === name) {
        sfdrLevel = item.sfdrLevel;
        break;
      }
    }
    return ({id, name, sfdrLevel, change: null, valuation: null, m: null, y: null, y3: null, y5: null, yMax: null,
    color: agColors.silverChalice, bgColor: agColors.mercury})}

  calcChange = (index: number, date: Date, dateMin: Date, values: Array<{ valuation: number, date: Date }>, valuation: number): ?number => {
    index && formatDate(date) !== formatDate(values[index].date) && index--
    if (moment(date).isSameOrAfter(dateMin, 'day')) {
      const { valuation: val0 } = values[index]
      return calculateChange(val0, valuation)
    }
    return null
  }

  prepareLegendData = (): LegendData => {
    const { data } = this.props
    const { unselected } = this.state
    const { dateRange: [dateL, dateR] } = this
    return data.map(({ id, name, sfdrLevel, values }) => {

      if (unselected.has(id)) {
        return this.unselected(id, name)
      }
      const dateLL = values[0].date

      if (moment(dateR).isBefore(dateLL, 'day')) {
        return this.unselected(id, name)
      }

      const [dateM, dateY, dateY3, dateY5] =
          [1, 12, 36, 60]
              .map(m =>
                  moment(dateR).subtract(m, 'months').toDate()
              )

      let dateMax = values !== undefined ? values.map(v => v.date)[0] : undefined

      let [iL, iR, iM, iY, iY3, iY5, iMax] = [dateL, dateR, dateM, dateY, dateY3, dateY5, dateMax].map(d => bisect(values, d))



      if (!values[iL] || !values[iR] || !values[iM] || !values[iY] || !values[iY3] || !values[iY5] || !values[iMax] ) {
        return this.unselected(id, name)
      }

      iL && formatDate(dateL) !== formatDate(values[iL].date) && iL--

      let { valuation } = values[iR]
      const { valuation: val0 } = values[iL]
      let change = calculateChange(val0, valuation)

      const [m, y, y3, y5, yMax] = [{i: iM, d: dateM}, {i: iY, d: dateY}, {i: iY3, d: dateY3}, {i: iY5, d: dateY5}, {i: iMax, d: dateMax }].map(({i, d}) => this.calcChange(i, d, dateLL, values, valuation))
      
      return {id, name, change, valuation, m, y, y3, y5, yMax, color: agColors.black, bgColor: colors(id), sfdrLevel}
    })
  }

  render () {
    const maxRange = (this.x2 && this.x2.domain()) || [new Date(), new Date()]
    const { display, data, urlTemplate, activeFilters, lang, width, height, appId, mode, initial } = this.props
    const { chart,  buttonGroup, legend } = display
    const { yValue, timeSlot } = this.state
    const { dateRange, filterDate, header, changeYValue, handleLegendClick } = this
    const legendData = this.prepareLegendData()
    const { change } = labels[lang]
    const val = labels[lang]['val'+ capitalize(mode)]
    const renderInitBox = data.length === 0
    const section = width < 768 || (width >= 992 && width < 1200)
    return (
      <div className="ui stackable grid ac-chart">
        {(buttonGroup || legend) && <div className="right aligned row toggle-row">
          <div className="column">
            {<ToggleIcon element={header} />}
          </div>
        </div>}
        <div className="row ac-chart__header">
          <div className={`bottom aligned ${width >= 1200 ? 'eight' : width >= 992 ? 'five' : 'sixteen'} wide column`}>
            {buttonGroup && <div className="ui grid buttongroup">
              <div className="row" style={{
                flexDirection: section && 'column',
                paddingTop: '0px',
                paddingBottom: '0px'
              }}>
                <div className="eight wide column" style={{
                  marginBottom: section && '1em'
                }}>
                  <TimeSlots
                    range={dateRange}
                    maxRange={maxRange}
                    filterDate={filterDate}
                    timeSlot={timeSlot}
                    lang={lang}
                  />
                </div>
                <div className="eight wide column">
                  <div style={{
                    float: section && 'left'
                  }}>
                    <DatePicker
                      range={dateRange}
                      maxRange={maxRange}
                      filterDate={filterDate}
                    />
                  </div>
                </div>
              </div>
              <div className="row" style={{
                paddingBottom: width >= 992 && '0',
                flexDirection: section && 'column'
              }}>
                <div className="eight wide column" style={{
                  order: section && '2'
                }}>
                  <YValueButtons
                    yValue={yValue}
                    val={val} 
                    change={change} 
                    onClick={changeYValue}
                  />
                </div>
                <div className="eight wide column" style={{
                  marginBottom: section && '1em'
                }}>
                  <div className="ui secondary right floated buttons" style={{
                    float: section && 'left'
                  }}>
                    <Dashboard
                      funds={data.map(({ id }) => id)}
                      initial={initial}
                      dateRange={dateRange}
                      urlTemplate={urlTemplate}
                      activeFilters={activeFilters}
                      lang={lang}
                    />
                  </div>
                </div>
              </div>
            </div>}
          </div>
          {legend && <div className={`bottom aligned ${width >= 1200 ? 'eight' : width >= 992 ? 'eleven' : 'sixteen'} wide column`}>
            <Legend
              data={legendData}
              lang={lang}
              width={width}
              date={dateRange.map(d => moment(d).format('L')).join(' - ')}
              onClick={handleLegendClick}
              mode={mode}
            />
          </div>}
        </div>
        {chart && <div className="right aligned row toggle-row">
          <div className="column">
            <ToggleIcon element={this.chart} />
          </div>
        </div>}
        {chart && <div className="row ac-chart__chart">
          <svg />
          {renderInitBox
          && <InitBox
            lang={lang}
            width={width}
            height={height}
            appId={appId}
          />}
          <div className='ac-chart-tooltip'></div>
          <div className='ac-chart-bar'></div>
        </div>}
      </div>
    )
   }
}

export default Chart

type YValueProps = {
  yValue: YValue,
  change: string,
  val: string,
  onClick: (name: YValue) => () => void
}

class YValueButtons extends PureComponent<YValueProps> {
  render () {
    const { yValue, change, val, onClick } = this.props
    return (
      <div className="ui primary buttons">
        <div className={`ui ${yValue === 'change' ? 'active' : ''} button`} onClick={onClick('change')}>
          {change}
        </div>
        <div className={`ui ${yValue === 'valuation' ? 'active' : ''} button`} onClick={onClick('valuation')}>
          {val}
        </div>
      </div>
    )
  }
}





// WEBPACK FOOTER //
// ./src/Chart.js